import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Tooltip,
  message
} from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SECTIONS_ALL_KEYS } from 'constants/permissions';
import {
  createRole,
  fetchPermissionsByRoleName,
  fetchRoleById,
  updateRole
} from 'services/api/roles';
import { PermissionRow, RoleDetail, RoleInput } from 'types/api/roles';
import { useUIDispatchContext } from 'providers/UI';
import { useAppSelector } from 'store/hooks';
import { selectUserData } from 'store/user/selectors';

import { Loading, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import useCeiling from 'hooks/useCeiling';
import { checkPermission } from 'utils/permission';
import { PermissionMap, sectionsExceedingCeiling } from 'utils/ceiling';

const RoleDetails: FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [isLoading, setLoading] = useState(!!id);
  const [roleName, setRoleName] = useState('');
  const [values, setValues] = useState<Record<string, number>>({});

  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // Role writes are open to every admin now, bounded by what they hold.
  const { isSuperAdmin, canGrant, permissions: ownPermissions } = useCeiling();

  const { toggleModal } = useUIDispatchContext();

  /**
   * How many admins hold this role — server-computed, one grouped count over
   * core.user_roles, carried on GET /roles/:id as `holderCount`.
   *
   * null means "not told", not "nobody": an older API omits the field, and a
   * banner reading "No admin holds this role" over a role eighteen people are
   * on is worse than no banner. Counting the admins list instead is not an
   * option — that list is scoped to what the operator may see, so a delegated
   * admin would be given a number far below the truth.
   */
  const [holderCount, setHolderCount] = useState<number | null>(null);
  const [role, setRole] = useState<RoleDetail | null>(null);
  const userRoleName = useAppSelector(selectUserData())?.role_name as
    | string
    | undefined;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const role = await fetchRoleById(id);
        if (cancelled) return;

        // No guard on the role shape: roles are flat, so there is no
        // parentId to key off. The previous version only populated the form
        // when parentId was set, which stopped working the moment it became
        // null for every role.
        setRoleName(role?.name ?? '');
        setHolderCount(
          typeof role?.holderCount === 'number' ? role.holderCount : null
        );
        setRole(role ?? null);
        form.setFieldsValue({
          name: role?.name,
          label: role?.label ?? role?.name
        });

        const permissionValues = (role?.rolePermissions ?? []).reduce<
          Record<string, number>
        >((acc, val) => {
          const pid = val?.permissionId;
          if (pid == null) return acc;
          const current = acc[pid] || 0;

          // Number(): `value` is a bigint column and arrives as a string,
          // so + would concatenate masks instead of adding them.
          return { ...acc, [pid]: current + Number(val.value) };
        }, {});

        setValues(permissionValues);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;

        // A 403 is an ordinary answer here, not a breakage: the role is a
        // system role, or it grants more than this operator holds. The
        // server's sentence names the sections, so show it rather than ours.
        // Leaving the spinner up — which is what returning here used to do —
        // left the operator on a page that never resolved.
        showErrorMessage(err);
        navigate('/access/roles', { replace: true });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form, navigate]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // The catalog is the same for every role — the backend validates the
        // name for existence and does not filter by it. Use this role's own
        // name so it stays correct if that ever changes; on create there is
        // no role yet, so fall back to the actor-type role, which is
        // is_system and always present.
        const rows = await fetchPermissionsByRoleName(roleName || 'admin');
        if (!cancelled) setPermissions(rows);
      } catch {
        // swallow — empty checkbox grid on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [roleName]);

  /**
   * The grid as the ceiling sees it: section -> OR of the masks ticked in
   * that section. Derived from the catalog, which carries the section for
   * each permission id — `values` alone is keyed by permission id, which is
   * a different keyspace.
   */
  const sectionMap = useMemo<PermissionMap>(() => {
    const map: PermissionMap = {};

    permissions.forEach(permission => {
      const mask = Number(values[permission.id] ?? 0);

      if (!mask) return;

      map[permission.section] = Number(map[permission.section] ?? 0) | mask;
    });

    return map;
  }, [permissions, values]);

  /**
   * One test covers BOTH ends the backend guards separately: `values` starts
   * as the role's current grid and mutates as boxes are ticked, so this is
   * false both when the role already grants more than the operator holds and
   * when they try to raise it past their own ceiling. Either alone would let
   * an admin climb.
   */
  const exceeded = isSuperAdmin
    ? []
    : sectionsExceedingCeiling(ownPermissions, sectionMap);

  /**
   * The ceiling as it stands RIGHT NOW, which is not what pageRefusal below
   * decided on arrival: `values` mutates as boxes are ticked, so an operator
   * who may edit this role can still raise it past their own ceiling while
   * editing. That is why this stays a live test and not a one-off gate.
   *
   * A system role never reaches here — pageRefusal sends it back to the list,
   * because RolePolicy refuses `edit` on one before any ceiling test and
   * before the superadmin exemption, so nobody may save one.
   */
  const blockedReason = exceeded.length
    ? String(t('messages.roles_above_your_ceiling'))
    : '';

  /**
   * MAY THIS OPERATOR OPEN THIS PAGE AT ALL?
   *
   * THE SERVER DECIDES NOW. GET /roles/:id runs the same policy as the write
   * routes and 403s for a system role or one above the caller's ceiling, so
   * reaching this line at all means the read was allowed. Editing the id in
   * the URL — /access/roles/details/36 changed to /29 — used to walk straight
   * into the super_admin editor for any authenticated admin, because the list
   * hid the control and the route served the row anyway.
   *
   * ONE CASE SURVIVES THE 403, which is why this is not empty: a superadmin
   * MAY READ a system role but may not edit one — nobody may, or the platform
   * can be locked out of its own administration. So the page opens for them
   * and has nothing to offer, and we send them back rather than present an
   * editor whose Save can only fail.
   */
  const pageRefusal =
    role && role.isSystem ? String(t('messages.roles_system_role')) : '';

  useEffect(() => {
    if (!pageRefusal) return;

    message.warning(pageRefusal);
    navigate('/access/roles', { replace: true });
  }, [pageRefusal, navigate]);

  const holders = holderCount ?? 0;

  /**
   * Compared by NAME, because /auth/me carries `role_name` and no role id —
   * `role` there is the literal actor type 'admin' for everyone. `roleName`
   * is the name as LOADED, so renaming the role in the form does not make
   * the operator stop recognising their own.
   */
  const isOwnRole = !!roleName && userRoleName === roleName;

  const holderSentence =
    holders > 0
      ? String(t('messages.role_holders', { count: holders }))
      : String(t('messages.role_holders_empty'));

  /**
   * What saving actually does to the people on this role. PUT /roles/:id ends
   * every holder's sessions — a label-only edit too — so this is not a
   * formality: an operator editing a role eighteen people hold is signing
   * eighteen people out.
   */
  const saveConsequence = [
    holders > 0 ? String(t('messages.role_holders_sign_out')) : '',
    holders > 0 && isOwnRole
      ? String(t('messages.role_holders_includes_you'))
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  const saveRole = useCallback(
    async (input: RoleInput) => {
      try {
        setIsSaving(true);

        if (id) {
          await updateRole(id, input);
        } else {
          await createRole(input);
        }

        message.success(
          String(
            t(
              id
                ? 'messages.successfully_updated'
                : 'messages.successfully_created'
            )
          )
        );
        navigate('/access/roles');
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    [id, navigate, t]
  );

  // antd runs the field rules before onFinish, so the confirmation is only
  // ever raised over an edit that would actually go through — asking first
  // and then failing validation would teach the operator to click past it.
  const onFinish = useCallback(
    (data: Record<string, string | number>) => {
      const input: RoleInput = {
        name: data.name as string,
        // `label` is the display name; `name` stays the identifier the
        // backend matches on. parentId is no longer accepted and would 400.
        label: (data.label as string) || (data.name as string),
        values
      };

      toggleModal('confirm', true, {
        title: id ? 'Save role' : 'Create role',
        message: id
          ? [
              String(t('messages.role_save_confirm')),
              holderSentence,
              saveConsequence
            ]
              .filter(Boolean)
              .join(' ')
          : String(t('messages.role_create_confirm')),
        onConfirm: () => {
          saveRole(input);
        }
      });
    },
    [id, values, t, toggleModal, saveRole, holderSentence, saveConsequence]
  );

  /**
   * Section names are a local map, but the catalogue is generated on the
   * backend and grows — `admins` arrived as section 35 after this map was
   * written. An unknown id used to render the raw key "permissions.undefined"
   * as the card heading, so fall back to naming the section by number.
   */
  const sectionTitle = (section: number): string => {
    const key = SECTIONS_ALL_KEYS[section];

    return key ? t(`permissions.${key}`) : `Section ${section}`;
  };

  const onChangePermission = (
    permission: PermissionRow,
    checked: boolean,
    value: number | string
  ) => {
    const { id } = permission;
    const bit = Number(value);

    setValues(prev => {
      const current = Number(prev[id]) || 0;
      const result = checked ? current + bit : current - bit;

      if (!result) {
        delete prev[id];

        return { ...prev };
      }

      return {
        ...prev,
        [id]: result
      };
    });
  };

  if (isLoading || pageRefusal) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      title={id ? 'Edit role' : 'Create Role'}
      onClickBack={() => navigate('/access/roles')}
      extra={[
        <Tooltip key="create-role" title={blockedReason}>
          <Button
            type="primary"
            loading={isSaving}
            disabled={!!blockedReason}
            onClick={() => form.submit()}
          >
            {id ? 'Edit' : 'Create'}
          </Button>
        </Tooltip>
      ]}
    >
      {/*
        Shown on edit only, and only when the server told us a number. On
        create there is nobody to warn, and a role with no holders still gets
        the line — "how many people is this about" is the question, and zero
        is an answer.
      */}
      {!!id && holderCount !== null && (
        <Alert
          showIcon
          style={{ marginBottom: 16 }}
          type={holders > 0 ? 'warning' : 'info'}
          message={holderSentence}
          description={saveConsequence || undefined}
        />
      )}
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        <Row gutter={16}>
          <Col sm={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col sm={12}>
            <Form.Item name="label" label="Label">
              <Input placeholder="Display name, e.g. Content Manager" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {!!permissions.length && (
        <Row gutter={[16, 16]} align="stretch">
          {permissions.map(permission => (
            <Col span={8} key={`permission-${permission.id}`}>
              <Card
                title={sectionTitle(permission.name)}
                headStyle={{ fontSize: 18 }}
                style={{ height: '100%' }}
              >
                <Row gutter={[0, 12]}>
                  {permission.values.map(value => {
                    const checked = checkPermission(
                      value.value,
                      values[permission.id]
                    );

                    // A bit the operator does not hold cannot be ticked at
                    // all. Letting them tick it and collecting a 403 on save
                    // loses the rest of their edit with it. Already-ticked
                    // bits stay interactive so an over-ceiling role can still
                    // be brought DOWN — the backend allows that, and it is
                    // the only way to repair one.
                    const grantable =
                      checked || canGrant(permission.section, value.value);

                    return (
                      <Col span={24} key={`${permission.id}-value-${value.id}`}>
                        <Tooltip
                          title={
                            grantable
                              ? ''
                              : String(
                                  t('messages.permission_above_your_ceiling')
                                )
                          }
                        >
                          <Checkbox
                            checked={checked}
                            disabled={!grantable}
                            onChange={e =>
                              onChangePermission(
                                permission,
                                e.target.checked,
                                value.value
                              )
                            }
                          >
                            {t(`permissions.${value.name}`)}
                          </Checkbox>
                        </Tooltip>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </PageWrapper>
  );
};

export default RoleDetails;
